import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

const API_URL = 'https://ufb-1.onrender.com/api/inventory';

const SIZES = ['XS', 'S', 'M', 'L', 'XL', 'XXL'];

const AddInventory = () => {
  const [form, setForm] = useState({ 
    productName: '', 
    gsm: '', 
    stockBySize: {},
    stockThresholds: {}
  });
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [newSize, setNewSize] = useState('M');
  const [newStock, setNewStock] = useState('');
  const [newThreshold, setNewThreshold] = useState('');
  const navigate = useNavigate();

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const addSize = () => {
    if (!newSize || newStock === '') {
      setError('Please select a size and enter stock amount');
      return;
    }

    if (Object.keys(form.stockBySize).includes(newSize)) {
      setError(`Size ${newSize} is already added`);
      return;
    }

    setForm({
      ...form,
      stockBySize: { ...form.stockBySize, [newSize]: Number(newStock) },
      stockThresholds: { ...form.stockThresholds, [newSize]: Number(newThreshold) || 0 }
    });
    setNewStock('');
    setNewThreshold('');
    setError(null);

    // Update newSize to the next available size (if any)
    const nextAvailable = availableSizes.filter(size => size !== newSize)[0] || '';
    setNewSize(nextAvailable);
  };

  const removeSize = (size) => {
    const newStockBySize = { ...form.stockBySize };
    const newStockThresholds = { ...form.stockThresholds };
    delete newStockBySize[size];
    delete newStockThresholds[size];
    setForm({ ...form, stockBySize: newStockBySize, stockThresholds: newStockThresholds });
  };

  const updateSizeStock = (size, stockCount) => {
    setForm({
      ...form,
      stockBySize: { ...form.stockBySize, [size]: Number(stockCount) }
    });
  };

  const updateSizeThreshold = (size, threshold) => {
    setForm({
      ...form,
      stockThresholds: { ...form.stockThresholds, [size]: Number(threshold) }
    });
  };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedImage(file);
      setImagePreview(URL.createObjectURL(file));
    } else {
      setSelectedImage(null);
      setImagePreview(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    
    if (!selectedImage) {
      setError('Please select an image');
      setLoading(false);
      return;
    }
    
    if (Object.keys(form.stockBySize).length === 0) {
      setError('Please add at least one size');
      setLoading(false);
      return;
    }

    try {
      // Create FormData with image and product data
      const formData = new FormData();
      formData.append('image', selectedImage);
      formData.append('productData', JSON.stringify({
        productName: form.productName,
        gsm: form.gsm,
        stockBySize: form.stockBySize,
        stockThresholds: form.stockThresholds
      }));

      // Create the product with image
      const res = await fetch(`${API_URL}/create`, {
        method: 'POST',
        body: formData
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.error || 'Failed to create product');
      }

      const result = await res.json();
      
      if (result.success) {
        navigate('/');
      } else {
        throw new Error('Failed to create product');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const availableSizes = SIZES.filter(size => !Object.keys(form.stockBySize).includes(size));

  return (
    <div className="max-w-2xl mx-auto p-10 bg-white rounded-2xl shadow-2xl mt-10 border border-blue-100">
      <h2 className="text-3xl font-extrabold mb-6 text-center text-blue-700 tracking-tight">Add New Product</h2>
      
      <form onSubmit={handleSubmit} className="flex flex-col gap-6">
        {/* Product Details */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <input 
            name="productName" 
            value={form.productName} 
            onChange={handleChange} 
            placeholder="Product Name" 
            className="border-2 border-blue-200 p-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 bg-slate-50 text-blue-900 font-medium" 
            required 
          />
          <input 
            name="gsm" 
            value={form.gsm} 
            onChange={handleChange} 
            placeholder="GSM" 
            className="border-2 border-blue-200 p-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 bg-slate-50 text-blue-900 font-medium" 
            required 
          />
        </div>

        {/* Image Upload */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Product Image *</label>
          <input 
            type="file" 
            accept="image/*" 
            onChange={handleImageSelect}
            className="border-2 border-blue-200 p-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 bg-slate-50"
            required
          />
          {selectedImage && (
            <p className="text-sm text-green-600 mt-1">✓ {selectedImage.name} selected</p>
          )}
          {!selectedImage && (
            <p className="text-sm text-red-500 mt-1">Image is required</p>
          )}
          {imagePreview && (
            <div className="mt-3">
              <img src={imagePreview} alt="Preview" className="w-32 h-32 object-cover rounded-lg border mx-auto" />
              <p className="text-xs text-gray-500 text-center mt-1">Image Preview</p>
            </div>
          )}
        </div>

        {/* Sizes Section */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-4">Product Sizes & Stock Thresholds</label>
          
          {/* Add New Size */}
          <div className="flex gap-3 mb-4 p-4 bg-gray-50 rounded-lg">
            <select 
              value={newSize} 
              onChange={(e) => setNewSize(e.target.value)}
              className="border-2 border-blue-200 p-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white flex-1"
              disabled={availableSizes.length === 0}
            >
              {availableSizes.map(size => (
                <option key={size} value={size}>{size}</option>
              ))}
            </select>
            <input 
              type="number" 
              min="0"
              value={newStock} 
              onChange={(e) => setNewStock(e.target.value)}
              placeholder="Stock" 
              className="border-2 border-blue-200 p-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white w-24"
            />
            <input 
              type="number" 
              min="0"
              value={newThreshold} 
              onChange={(e) => setNewThreshold(e.target.value)}
              placeholder="Threshold" 
              className="border-2 border-blue-200 p-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white w-24"
            />
            <button 
              type="button" 
              onClick={addSize}
              disabled={availableSizes.length === 0 || newStock === '' || !newSize}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Add New
            </button>
          </div>

          {availableSizes.length === 0 && (
            <p className="text-sm text-gray-500 text-center py-2">All sizes have been added</p>
          )}
          
          {/* Existing Sizes */}
          {Object.keys(form.stockBySize).length === 0 && (
            <div className="text-center text-gray-500 py-4">No sizes added yet. Use the form above to add sizes.</div>
          )}
          
          <div className="space-y-3">
            {Object.entries(form.stockBySize).map(([size, stockCount]) => (
              <div key={size} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <span className="text-lg font-semibold text-gray-800 min-w-[40px]">{size}</span>
                <div className="flex flex-col gap-1">
                  <label className="text-xs text-gray-600">Stock</label>
                  <input 
                    type="number" 
                    min="0"
                    value={stockCount} 
                    onChange={(e) => updateSizeStock(size, e.target.value)}
                    placeholder="Stock" 
                    className="border-2 border-blue-200 p-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white w-24"
                    required
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-xs text-gray-600">Threshold</label>
                  <input 
                    type="number" 
                    min="0"
                    value={form.stockThresholds[size] || ''} 
                    onChange={(e) => updateSizeThreshold(size, e.target.value)}
                    placeholder="Threshold" 
                    className="border-2 border-blue-200 p-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white w-24"
                  />
                </div>
                <button 
                  type="button" 
                  onClick={() => removeSize(size)}
                  className="px-3 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 font-semibold"
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        </div>

        {error && <div className="text-red-500 text-center font-semibold">{error}</div>}
        
        <button 
          type="submit" 
          disabled={loading || !selectedImage}
          className="bg-gradient-to-r from-blue-700 to-blue-400 text-white p-3 rounded-lg font-bold text-lg hover:scale-105 transition disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? 'Creating Product...' : 'Create Product'}
        </button>
      </form>
    </div>
  );
};

export default AddInventory;